import * as Sentry from '@sentry/node'
import { ELanguage } from '@microfrontends/react-components'
import { ICompanyInfo } from '../components/CompanyDetail/type'
import { decrypt } from '../helpers/encode'
import { DEFAULT_ERROR_CODE } from './jobService'
import { TCompanyId } from '../helpers/urlHelper'

const COMPANY_DETAIL_API = process.env.NEXT_PUBLIC_MS_BASE_PATH + '/company-profile/v1.0/company-profile/detail'
const COMPANY_OCI_API = process.env.NEXT_PUBLIC_MS_BASE_PATH + '/company-profile/v1.0/oci'
const COMPANY_DETAIL_API_V2 = process.env.NEXT_PUBLIC_MS_BASE_PATH + '/company-profile/v1.0/company-info'
const COMPANY_FOLLOW_API = `${process.env.NEXT_PUBLIC_MS_BASE_PATH}/api-gateway/v1.0/profile/follow`
export interface ICompanyDetailResponse {
    meta: {
        code: number
        message: string
    }
    data: ICompanyInfo | null
}

export enum EBenefitName {
    Bonus = 'Bonus',
    HealthcarePlan = 'Healthcare Plan',
    PaidLeave = 'Paid Leave',
    Training = 'Training',
    Awards = 'Awards',
    Library = 'Library',
    Mobile = 'Mobile',
    TravelOpportunities = 'Travel Opportunities',
    TeamActivities = 'Team Activities',
    Transportation = 'Transportation',
    Canteen = 'Canteen',
    Vochers = 'Vochers',
    Kindergarten = 'Kindergarten',
    Others = 'Others'
}

export type TBenefit = {
    benefitId: number
    benefitName: EBenefitName
    benefitDescription: string
}

export type TInfoCompanyOCI = {
    coProfileId?: number | null
    benefits?: TBenefit[] | null
    headquaterAddress?: string | null
    branchAddress?: string[] | null
    companyImages?: string[] | null
    companyVideos?: string[] | null
    companyProfile?: string | null
    companySize?: string | null
    companyContact?: string | null
    companyLogo?: string | null
    companyName?: string | null
    bannerDesktopUri?: string | null
}

export const getCompanyOCIInfoFromId = async (companyId: number, langCode?: ELanguage): Promise<TInfoCompanyOCI | null> => {
    if (!companyId) {
        return null
    }
    
    try {
        const response = await fetch(`${COMPANY_OCI_API}/${companyId}?profilePublishedSiteMask=1`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Accept-Language': langCode || ELanguage.VI
            }
        })
        const json = await response.json()
        const data =json.data
        
        if (!data.attributes.coProfileId) {
            return null
        }
        return data.attributes
        
    } catch (e) {
        return null
    }
}

export const companyDetailService = async (
    data: { type: string; id: string },
    userId?: number,
    langCode?: ELanguage
): Promise<ICompanyDetailResponse> => {
    try {
        const requestOptions = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Accept-Language': langCode || ELanguage.VI
            }
        }

        langCode = langCode || ELanguage.VI
        let url = COMPANY_DETAIL_API_V2 + `?companyId=${data.id}&languageCode=${langCode}&userId=${userId}`
        if (data.type === TCompanyId.EMPLOYER_ID) {
            url = COMPANY_DETAIL_API_V2 + `?employerId=${data.id}&languageCode=${langCode}&userId=${userId}`
        }
        const responseCompanyDetail = await fetch(url, requestOptions)

        let responseCompany = await responseCompanyDetail.json()
        responseCompany.data = formatCompanyDetail(responseCompany.data)

        return responseCompany
    } catch (err) {
        Sentry.captureException(err)
        return {
            meta: {
                code: DEFAULT_ERROR_CODE,
                message: 'ERROR'
            },
            data: null
        }
    }
}

export interface ICompanyFollowResponse {
    meta?: {
        code: number
        message: string
    }
    data: any
    code?: number
    id?: number
    message?: string
}

export const companyFollow = async (
    companyId: number,
    isFollow: boolean,
    accessToken: string,
    langCode: ELanguage
): Promise<ICompanyFollowResponse> => {
    try {
        const postData = {
            companyId: companyId,
            status: isFollow ? 1 : 0
        }
        const decodeToken = decrypt(accessToken)
        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept-Language': langCode || ELanguage.VI,
                Authorization: `Bearer ${decodeToken}`
            },
            body: JSON.stringify(postData)
        }
        const response = await fetch(COMPANY_FOLLOW_API, requestOptions)
        return await response.json()
    } catch (err) {
        Sentry.captureException(err)
        return {
            meta: {
                code: DEFAULT_ERROR_CODE,
                message: 'ERROR'
            },
            data: null
        }
    }
}

/**
 * VNW-22972 [Web][Company detail] Change source to General info
 * Format detail to ICompanyInfo for rendering on client
 *
 * @param companyDetail
 * @return ICompanyInfo
 *
 * @author NPMHieu
 * @since Jul 4 2023
 */
const formatCompanyDetail = function (companyData: any): ICompanyInfo {
    let { attributes } = companyData
    let industryNames = ''
    let industries = []
    if (attributes.industries && Array.isArray(attributes.industries)) {
        industryNames = attributes?.industries?.map((item: any) => item.industryName).join(', ')
        industries = attributes?.industries?.map((item: any) => item.industryId)
    }

    let company = {
        companyId: companyData.id,
        address: attributes.headquaterAddress,
        bannerDesktopUri: attributes.bannerDesktopUri,
        clpInfo: null,
        companyBenefits: attributes.benefits,
        companyLogoURL: attributes.companyLogo,
        companyName: attributes.companyName,
        companyProfile: attributes.companyProfile,
        companySizeId: attributes.companySize,
        contactEmail: attributes.companyContact,
        contactName: attributes.companyContact,
        jobImageURLs: attributes.companyImages,
        jobVideoURLs: attributes.companyVideos,
        profileBaseName: attributes.profileBaseName || '',
        website: attributes.website || '',
        whyWorkWithUs: attributes.whyWorkWithUs || '',
        industries: industries,
        industryNames: industryNames,
        companySizeName: attributes.companySize,
        isFollowed: Number(attributes.isFollowed) || 0,
        isReported: Number(attributes.isReported) || 0,
        totalFollow: attributes.followerCount || 0,
        totalJobs: attributes.totalJobs || 0,
        profilePublishedSiteMask: attributes.profilePublishedSiteMask || 0,
        profileSlug: attributes.profileSlug || '',
        url: attributes.url,
        geoloc: attributes.geoloc || null
    }

    return company
}
